import { z } from 'zod';
import type { SimNao } from '@/types';
import { attempt } from '@/std/result';

export const EmpresaConfigSeparacaoAutomaticaSistemasSchema = z
    .string()
    .nullish()
    .transform((value, ctx) => {
        const parsed = attempt(() => JSON.parse(value || '[]'));

        if (parsed.error) {
            ctx.addIssue({
                code: 'custom',
                message: 'Lista de sistemas para separação automática inválida',
            });

            return z.NEVER;
        }

        return parsed.value;
    })
    .pipe(z.array(z.number()));

/** Indica quando serão atualizadas as parcelas de um | pedido | venda | nota fiscal | */
export enum EmpresaConfigVendaAtualizarVencimento {
    SemAtualizacao = 1,
    AoTransformarOrcamentoPedidoVenda = 2,
    AoGerarNfeNfce = 3,
    AoTransmitirNfeNfceEmissao = 4,
    AoTransmitirNfeNfceSaida = 5,
}

/** Indica se o módulo de separação é utilizado por esta empresa */
export enum EmpresaConfigSeparacao {
    NaoUtiliza = 1,
    UtilizaSeparacaoEConferencia = 2,
    UtilizaSomenteSeparacao = 3,
}

/** Indica quando um registro será enviado para a separação */
export enum EmpresaConfigSeparacaoEnvio {
    Manualmente = 1,
    AoInserirPedidoBonificacao = 2,
    AoInserirVendaBonificacao = 3,
}

export enum EmpresaConfigExpedicaoEnvio {
    Manualmente = 1,
    AoInserirPedido = 2,
    AoGerarVenda = 3,
    AoSalvarNotaFiscal = 4,
    AoAutorizarNotaFiscal = 5,
    AposSeparacao = 6,
    AposConferencia = 7,
}

export type ApiEmpresaConfiguracao = {
    emc_idpk: number;
    emc_empresa_idpk: number;

    /** Data da última consulta das notas para preenchimento no manifestador do destinatario de nfe */
    emc_md_ultimo_nsu: number;

    /** Última nfe consultada pelo manifestador */
    emc_md_ultima_consulta?: string;

    /**
     * 1 = manual;
     * 2 = Automaticamente ao inserir um novo pedido;
     * 3 = Automaticamente ao gerar uma venda ou transformar um orçamento e pedido em venda;
     * 4 = Automaticamente ao salvar o documento fiscal (nfe, nfce);
     * 5 = Automaticamente ao autorizar o documento fiscal (nfe, nfce);
     * 6 = Automaticamente após a separação do registro;
     * 7 = Automaticamente após a conferência do registro;
     */
    emc_expedicao_envio: EmpresaConfigExpedicaoEnvio;

    /**
     * Campo que indica o padrão de movimentação do estoque desta empresa com uma das seguintes formas:
     * M = Manualmente;
     * N = Ao autorizar uma NF;
     * V = Ao inserir uma venda;
     */
    emc_movimentar_estoque: 'M' | 'N' | 'V';

    /**
     * Campo que indica o padrão de movimentação do financeiro desta empresa com uma das seguintes formas:
     * M = Manualmente;
     * N = Ao autorizar uma NF;
     * V = Ao inserir uma venda;
     */
    emc_movimentar_financeiro: 'M' | 'N' | 'V';

    /** Campo que indica se calculará os impostos na inserção do orçamento/pedido/venda */
    emc_venda_calcular_impostos: SimNao;

    emc_venda_atualizar_vencimento: EmpresaConfigVendaAtualizarVencimento;

    /**
     * Caso a nota emitida possuir vínculo com uma venda externa,
     * para movimentar seu estoque será considerado a partir deste campo:
     * M = Movimentação manual;
     * R = Movimentação na remessa/recarga/retorno;
     * V = Movimentação nas vendas;
     */
    emc_movimentar_estoque_venda_externa: 'M' | 'R' | 'V';

    /** Campo que indica se pode ou não exibir total das contas em aberto do cliente destinatário **/
    emc_impressao_venda_contas_em_aberto: SimNao;

    /** Aceitar a duplicação de cadastros com mesmo documento fiscal */
    emc_pessoa_duplicar: SimNao;

    emc_venda_repassar_financeiro_categoria: SimNao;
    emc_venda_repassar_observacoes: SimNao;
    emc_venda_repassar_produto_detalhe: SimNao;
    emc_venda_repassar_data_hora_saida: SimNao;

    /**
        Se emc_venda_repassar_data_hora_saida = 'S', então este campo indicará quantos dias após a data da emissão que será informada a data de saída:
        - 0: Mesma data da emissão
        - 1: Data de saída 1 dia após a data de emissão
    */
    emc_venda_repassar_data_saida: 0 | 1;
    emc_venda_repassar_hora_saida: string;
    emc_venda_nfe_serie_idpk: number;
    emc_venda_nfce_serie_idpk: number;

    /**
        Dados de log
            emc_log_gerar: 
                - "S": gera o log
            emc_log_periodo
                - período em dias
    */

    emc_log_gerar: string;
    emc_log_periodo: number;

    emc_manifestador_dfe_consulta_automatica: SimNao;
    emc_manifestador_dfe_proxima_consulta: string;

    emc_conferencia_cegas: SimNao;
    emc_separacao: EmpresaConfigSeparacao;
    emc_separacao_automatica_sistemas: number[];
    emc_separacao_envio: EmpresaConfigSeparacaoEnvio;
    emc_separacao_ruptura: SimNao;

    emc_entregador_tabela_comissao_idpk: number;
    emc_tabela_comissao_idpk: number;

    emc_sped: number;
    emc_sped_perfil: 'A' | 'B' | 'C';
    emc_sped_tipo_atividade: number;
    emc_sped_tipo_industrial: number;
};
